import { getDomainLanguage } from "@/hooks/getDomain";
import eventsData from "./events.json";
import { getTranslations } from "./i18n";

const api_url = process.env.NEXT_PUBLIC_API_URL;
const api_key = process.env.NEXT_PUBLIC_API_KEY;

// --- CONFIG ---
// Adjust these if you like:
const DEFAULT_TIMEOUT_MS = 4000; // 4s for most endpoints
const SLOW_TIMEOUT_MS = 12000;    // for heavier endpoints if needed

// Build API URL from req + endpoint
const buildApiUrl = (req, endpoint, query) => {
  const [, countryCode] = getDomainLanguage(req);

  const refresh = new URL(
    req.url,
    `https://${req.headers.host}`
  ).searchParams.get("refresh") || "";

  const url =
    `${process.env.NEXT_PUBLIC_API_URL}${endpoint}` +
    `?country=${countryCode}` +
    `&sa=1015` +
    `&key=${process.env.NEXT_PUBLIC_API_KEY}` +
    (refresh ? `&refresh=${encodeURIComponent(refresh)}` : "") +
    (query ? `&${query}` : "");

  return url;
};

// Safely parse JSON from a response body
const safeJsonParseFromText = (text) => {
  try {
    return JSON.parse(text);
  } catch (e) {
    console.error("Invalid JSON response:", text);
    throw new Error("Invalid JSON response");
  }
};

// Generic helper: fetch JSON with a timeout
const fetchJsonWithTimeout = async (url, timeoutMs, label) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      throw new Error(
        `[${label}] HTTP error! status: ${response.status} url: ${url}`
      );
    }

    const text = await response.text();
    return safeJsonParseFromText(text);
  } catch (e) {
    if (e.name === "AbortError") {
      console.error(`[${label}] Request timed out after ${timeoutMs}ms`);
    } else {
      console.error(`[${label}] Error during fetch:`, e);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Convenience: build URL from req and call fetchJsonWithTimeout
const fetchApiJson = async (req, endpoint, query, timeoutMs, label) => {
  const url = buildApiUrl(req, endpoint, query);
  return fetchJsonWithTimeout(url, timeoutMs, label);
};

// ----------------------
// EXPORTS
// ----------------------

export const fetchTopOffers = async (req) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      "topoffers=true&data=deals",
      SLOW_TIMEOUT_MS,
      "fetchTopOffers"
    );
    return Object.values(data || {});
  } catch (e) {
    console.error("Error fetching top offers (fallback to []):", e);
    return [];
  }
};

export const fetchSlides = async (req) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      "homeslider=first&data=merchants",
      DEFAULT_TIMEOUT_MS,
      "fetchSlides"
    );
    return Object.values(data || {});
  } catch (e) {
    console.error("Error fetching slides (fallback to []):", e);
    return [];
  }
};

export const fetchAdverts = async (req) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      "homeslider=second&data=merchants",
      DEFAULT_TIMEOUT_MS,
      "fetchAdverts"
    );
    return Object.values(data || {});
  } catch (e) {
    console.error("Error fetching adverts (fallback to []):", e);
    return [];
  }
};

export const fetchMerchants = async (req) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      "data=merchants&adult=true",
      SLOW_TIMEOUT_MS,
      "fetchMerchants"
    );
    return Object.values(data || {});
  } catch (e) {
    console.error("Error fetching merchants (fallback to []):", e);
    return [];
  }
};

export const fetchSeasonalEvents = async (req) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      "seasonalevents=true",
      SLOW_TIMEOUT_MS,
      "fetchSeasonalEvents"
    );

    return Object.values(data || {});
  } catch (e) {
    console.error("Error fetching seasonal events (fallback to []):", e);
    return [];
  }
};

// Static event data — imported as JSON so webpack bundles it (no fs/path needed)
/**
 * Get currently active seasonal events for the sidebar links.
 * Reads from the static events.json — no API call needed.
 * Dates are month-day (MM-DD) recurring yearly.
 * Returns an array of currently active events (plus next upcoming if none active).
 */
export const fetchCurrentEvent = async (req) => {
  try {
    const [, countryCode] = getDomainLanguage(req);
    const events = eventsData[countryCode] || eventsData["us"] || [];
    if (!events.length) return [];

    const now = new Date();
    const year = now.getFullYear();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const parseDate = (md) => {
      const [m, d] = md.split("-").map(Number);
      return new Date(year, m - 1, d);
    };

    // 1. All currently active events
    const current = events.filter((ev) => {
      const start = parseDate(ev.start);
      let end = parseDate(ev.end);
      if (end < start) end = new Date(year + 1, end.getMonth(), end.getDate());
      return today >= start && today <= end;
    });
    if (current.length) return current;

    // 2. Next upcoming event
    const upcoming = events
      .filter((ev) => parseDate(ev.start) > today)
      .sort((a, b) => parseDate(a.start) - parseDate(b.start));
    if (upcoming.length) return [upcoming[0]];

    // 3. Fallback: first event
    return [events[0]];
  } catch (e) {
    console.error("Error in fetchCurrentEvent (fallback to []):", e.message);
    return [];
  }
};

export const fetchSeasonalEvent = async (req, slug) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      `seasonalevent=true&slug=${encodeURIComponent(slug)}`,
      DEFAULT_TIMEOUT_MS,
      "fetchSeasonalEvent"
    );

    return data;
  } catch (e) {
    console.error("Error fetching seasonal event page (fallback to null):", e);
    return null;
  }
};

export const fetchDiscounts = async ( req, storeId, type = "store") => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      `id=${storeId}&adult=true${type === "event" ? "&type=event" : ""}`,
      SLOW_TIMEOUT_MS,
      "fetchDiscounts"
    );

    if (data && data.offers) {
      const offers = { ...data.offers };

      if (data.highlight) {
        const firstOfferKey = Object.keys(offers)[0];
        if (firstOfferKey) {
          offers[firstOfferKey].highlight = data.highlight;
        }
      }

      return Object.values(offers);
    }

    return [];
  } catch (e) {
    console.error("Error fetching discounts (fallback to []):", e);
    return [];
  }
};

export const fetchTranslations = async (req) => {
  const [, countryCode] = getDomainLanguage(req);
  return getTranslations(countryCode);
};

export const fetchStore = async (req, term, type = "store") => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      `adult=true&q=${encodeURIComponent(term)}&type=${type}`,
      DEFAULT_TIMEOUT_MS,
      "fetchStore"
    );

    return data;
  } catch (e) {
    console.error("Error fetching store (fallback to null):", e);
    return null;
  }
};

// This one does not use `req`, so we build URL manually and still use a timeout:
export const fetchStoreByTerm = async (countryCode, language, term) => {
  const url = `https://dashboard.imp-multimedia.com/export/api.php?country=${countryCode}&sa=1015&key=An8WNzDYVeMjdw6hpRFrXQ&adult=true&q=${
    term ? encodeURIComponent(term) : ""
  }`;

  try {
    const data = await fetchJsonWithTimeout(
      url,
      DEFAULT_TIMEOUT_MS,
      "fetchStoreByTerm"
    );
    return data;
  } catch (e) {
    console.error("Error fetching store by term (fallback to null):", e);
    return null;
  }
};

export const fetchPromoAgent = async (req, storeName, slug) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      `action=promoagent&program=${encodeURIComponent(storeName)}&slug=${encodeURIComponent(slug)}`,
      SLOW_TIMEOUT_MS,
      "fetchPromoAgent"
    );
    return data;
  } catch (e) {
    console.error("Error fetching promo agent (fallback to null):", e);
    return null;
  }
};

export const fetchRandomStore = async (req, zsem, countryCode) => {
  try {
    const data = await fetchApiJson(
      req,
      "api.php",
      `zsem=${encodeURIComponent(zsem)}&rdstore=true&country=${encodeURIComponent(countryCode)}`,
      DEFAULT_TIMEOUT_MS,
      "fetchRandomStore"
    );
    return data;
  } catch (e) {
    console.error("Error fetching store (fallback to null):", e);
    return null;
  }
};
